import React from 'react';
import { FaClipboardList, FaCheckCircle, FaMapMarkerAlt, FaWrench, FaPause, FaFlagCheckered, FaWallet, FaTimesCircle, FaBan, FaSpinner } from 'react-icons/fa';
import './StatusTracker.css';

const STATUS_STEPS = [
    { key: 'pending', label: 'Requested', icon: <FaClipboardList />, updatedBy: 'system' },
    { key: 'accepted', label: 'Accepted', icon: <FaCheckCircle />, updatedBy: 'provider' },
    { key: 'reached-site', label: 'Reached Site', icon: <FaMapMarkerAlt />, updatedBy: 'provider' },
    { key: 'in-progress', label: 'Work in Progress', icon: <FaWrench />, updatedBy: 'provider' },
    { key: 'work-completed', label: 'Work Completed', icon: <FaFlagCheckered />, updatedBy: 'provider' },
    { key: 'payment-done', label: 'Payment Done', icon: <FaWallet />, updatedBy: 'customer' },
];

/**
 * Get the index of a status in the steps array, or -1 if not found.
 */
function getStepIndex(status) {
    // work-paused maps to same position as in-progress
    if (status === 'work-paused') return STATUS_STEPS.findIndex(s => s.key === 'in-progress');
    return STATUS_STEPS.findIndex(s => s.key === status);
}

/**
 * StatusTracker — visual progress bar for booking lifecycle.
 *
 * Props:
 *   currentStatus  – the booking's current status string
 *   statusHistory  – array of { status, timestamp, note }
 *   onAction       – (nextStatus) => void  — called when the action button is clicked
 *   role           – 'customer' | 'provider'
 *   actionLoading  – boolean — disables button while updating
 */
const StatusTracker = ({ currentStatus, statusHistory = [], onAction, role, actionLoading }) => {
    const currentIdx = getStepIndex(currentStatus);

    // Determine next step for this role
    const nextStep = STATUS_STEPS[currentIdx + 1] || null;
    const canAct = nextStep && nextStep.updatedBy === role && onAction;

    // Find timestamp for a given status from history
    const getTimestamp = (key) => {
        const entry = [...statusHistory].reverse().find(h => h.key === key || h.status === key);
        if (!entry) return null;
        const ts = entry.timestamp;
        try {
            return new Date(ts).toLocaleString('en-IN', {
                day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
            });
        } catch {
            return ts;
        }
    };

    // Cancelled / Rejected — show terminated state
    const isTerminated = currentStatus === 'cancelled' || currentStatus === 'rejected';
    const isPaused = currentStatus === 'work-paused';

    return (
        <div className="status-tracker">
            <div className="tracker-steps">
                {STATUS_STEPS.map((step, idx) => {
                    const isDone = idx <= currentIdx;
                    const isCurrent = idx === currentIdx;
                    const isFuture = idx > currentIdx;
                    const ts = getTimestamp(step.key);

                    return (
                        <div
                            key={step.key}
                            className={`tracker-step ${isDone ? 'done' : ''} ${isCurrent ? 'current' : ''} ${isFuture ? 'future' : ''} ${isTerminated && isCurrent ? 'terminated' : ''} ${isPaused && step.key === 'in-progress' ? 'paused' : ''}`}
                        >
                            {/* Connector line (not for first step) */}
                            {idx > 0 && (
                                <div className={`tracker-connector ${isDone ? 'filled' : ''}`}></div>
                            )}

                            <div className="tracker-dot">
                                <span className="tracker-icon">{isDone ? step.icon : (idx + 1)}</span>
                            </div>

                            <div className="tracker-label">
                                <span className="tracker-step-name">{step.label}</span>
                                {ts && isDone && <span className="tracker-time">{ts}</span>}
                                {!isDone && (
                                    <span className="tracker-who">
                                        {step.updatedBy === 'provider' ? 'By worker' : step.updatedBy === 'customer' ? 'By you' : ''}
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Terminated state */}
            {isTerminated && (
                <div className="tracker-terminated">
                    <span className="terminated-icon">{currentStatus === 'cancelled' ? <FaTimesCircle /> : <FaBan />}</span>
                    <span>Booking {currentStatus}</span>
                </div>
            )}

            {/* Paused state */}
            {isPaused && (
                <div className="tracker-paused">
                    <FaPause /> Work paused — your worker will resume next session
                </div>
            )}

            {/* Action button for next step */}
            {canAct && !isTerminated && (
                <div className="tracker-action">
                    <button
                        className="tracker-action-btn"
                        onClick={() => onAction(nextStep.key)}
                        disabled={actionLoading}
                    >
                        {actionLoading
                            ? <><FaSpinner className="spin-icon" /> Updating…</>
                            : <>{nextStep.icon} {nextStep.label}</>}
                    </button>
                    <span className="tracker-action-hint">
                        {role === 'customer' ? 'Tap to confirm' : 'Tap to update status'}
                    </span>
                </div>
            )}

            {/* Completed state */}
            {currentStatus === 'payment-done' && (
                <div className="tracker-completed-msg">
                    <FaCheckCircle /> Service completed & paid — Thank you!
                </div>
            )}
        </div>
    );
};

export { STATUS_STEPS, getStepIndex };
export default StatusTracker;
